import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { sportySdk } from './client';
import {
  type AllUserCustomClipsQueryVariables,
  type AssetOffsetsQueryVariables,
  type CerebroEventsQueryVariables,
  type GameMediaQueryVariables,
  type SearchQueryVariables,
  type UserClipsQueryVariables,
  type UserCustomClipsQueryVariables,
} from './generated/graphql';
import { toPlayResultNameMap } from './utils';

export const assetOffsetsKeys = {
  all: 'assetOffsets' as const,
};

export const assetOffsetsQueryOptions = (variables: AssetOffsetsQueryVariables) =>
  queryOptions({
    queryFn: async ({ signal }) => {
      const { assetOffsets } = await sportySdk.assetOffsets(variables, undefined, signal);
      if (
        !assetOffsets?.success ||
        !Number.isFinite(assetOffsets.startSeconds) ||
        !Number.isFinite(assetOffsets.endSeconds) ||
        assetOffsets.startSeconds < 0 ||
        assetOffsets.endSeconds <= assetOffsets.startSeconds
      ) {
        throw new Error('Unable to resolve valid video offsets for this play.');
      }
      return assetOffsets;
    },
    queryKey: [assetOffsetsKeys.all, variables],
    retry: false,
  });

export const availableWatermarksQueryOptions = () =>
  queryOptions({
    queryFn: () => sportySdk.availableWatermarks(),
    queryKey: ['availableWatermarks'],
    staleTime: Infinity,
  });

export const allowedSportsQueryOptions = () =>
  queryOptions({
    queryFn: () => sportySdk.allowedSports(),
    queryKey: ['allowedSports'],
    staleTime: Infinity,
  });

export const playClipsQueryOptions = ({ limit, page, query, searchType }: SearchQueryVariables) =>
  queryOptions({
    queryFn: () => sportySdk.search({ limit, page, query, searchType }),
    queryKey: ['playClips', query, page, limit, searchType],
    staleTime: 30 * 1000, // 30 seconds
  });

export const userClipsKeys = {
  all: 'userClips' as const,
};

export const userClipsOptions = ({ limit, page, sortBy, title }: UserClipsQueryVariables) =>
  queryOptions({
    queryFn: () => sportySdk.userClips({ limit, page, sortBy, title }),
    queryKey: [userClipsKeys.all, page, limit, sortBy, title],
    staleTime: 30 * 1000, // 30 seconds
  });

export const userCustomClipsKeys = {
  all: 'userCustomClips' as const,
  infinite: 'infinite' as const,
};

export const userCustomClipsOptions = ({
  description,
  gamePk,
  limit,
  page,
  sortOrder,
}: UserCustomClipsQueryVariables) =>
  queryOptions({
    queryFn: () => sportySdk.userCustomClips({ description, gamePk, limit, page, sortOrder }),
    queryKey: [userCustomClipsKeys.all, page, limit, sortOrder, gamePk, description],
    staleTime: 30 * 1000, // 30 seconds
  });

/* eslint-disable perfectionist/sort-objects */
export const userCustomClipsInfiniteQueryOptions = ({
  gamePk,
  limit,
  sortOrder,
}: Pick<UserCustomClipsQueryVariables, 'gamePk' | 'limit' | 'sortOrder'>) => {
  const pageSize = limit ?? 20;

  return infiniteQueryOptions({
    queryKey: [userCustomClipsKeys.all, userCustomClipsKeys.infinite, gamePk, pageSize, sortOrder],
    queryFn: ({ pageParam }) =>
      sportySdk.userCustomClips({ gamePk, limit: pageSize, page: pageParam, sortOrder }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const fetchedCount = allPages.reduce(
        (count, page) => count + (page.userCustomClips?.customClips?.length ?? 0),
        0,
      );
      const total = lastPage.userCustomClips?.total ?? 0;
      const lastPageCount = lastPage.userCustomClips?.customClips?.length ?? 0;
      return lastPageCount > 0 && fetchedCount < total ? allPages.length : undefined;
    },
    staleTime: 30 * 1000,
  });
};
/* eslint-enable perfectionist/sort-objects */

export const allUserCustomClipsKeys = {
  all: 'allUserCustomClips' as const,
};

export const allUserCustomClipsOptions = ({
  description,
  gamePk,
  limit,
  page,
  sortOrder,
}: AllUserCustomClipsQueryVariables) =>
  queryOptions({
    queryFn: () => sportySdk.allUserCustomClips({ description, gamePk, limit, page, sortOrder }),
    queryKey: [allUserCustomClipsKeys.all, page, limit, sortOrder, gamePk, description],
    staleTime: 30 * 1000,
  });

export const gameMediaQueryOptions = ({ gameDate, gamePk }: GameMediaQueryVariables) =>
  queryOptions({
    queryFn: () => sportySdk.gameMedia({ gameDate, gamePk }),
    queryKey: ['gameMedia', gameDate, gamePk],
    select: (data) => ({
      ...data,
      gameMedia: [...data.gameMedia].sort(
        (a, b) => Number(b.name.startsWith('DM5')) - Number(a.name.startsWith('DM5')),
      ),
    }),
  });

export const cerebroEventsQueryOptions = ({ eventTypes, startDate }: CerebroEventsQueryVariables) =>
  queryOptions({
    queryFn: () => sportySdk.cerebroEvents({ eventTypes, startDate }),
    queryKey: ['cerebroEvents', startDate, eventTypes],
    staleTime: 30 * 1000,
  });

export const playResultNamesQueryOptions = () => {
  return queryOptions({
    queryFn: () => sportySdk.playResultNames(),
    queryKey: ['playResultNames'],
    select: ({ fields }) => toPlayResultNameMap(fields),
    staleTime: Infinity,
  });
};
